'use client'

import { IPlant } from "@/models/types/plant";
import { ICycle } from "@/models/types/cycle";
import { Select } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { UseFormReturnType } from "@mantine/form";
import Link from "next/link";
import { useStateStore } from "@/context/stateStore";

export type CycleFormType = UseFormReturnType<{ cycle: ICycle; }, (values: {
    cycle: ICycle;
}) => {
    cycle: ICycle;
}>

export const CycleStage = ({ cycleForm, heightQuery }: { cycleForm: CycleFormType, heightQuery: number }) => {

    const heightToUse = heightQuery / 15;

    return (
        <Select
            id="cycle-stage"
            label="Stage"
            placeholder="Select stage"
            data={[
                { value: 'seed', label: 'Seed' },
                {value: 'seedling', label: 'Seedling' },
                { value: 'sapling', label: 'Sapling' },
                {value: 'juvenile', label: 'Juvenile' },
                { value: 'mature', label: 'Mature' },
                {value: 'fruiting', label: 'Fruiting' },
                {value: 'ripe', label: 'Ripe' },
                {value: 'dormant', label: 'Dormant' },
                {value: 'dying - perennial', label: 'Dying - Perennial' },
                {value: 'dying - annual', label: 'Dying - Annual' },
                {value: 'dormant - perennial', label: 'Dormant - Perennial' },
                {value: 'dormant - annual', label: 'Dormant - Annual' },
                { value: 'harvested', label: 'Harvested' }
            ]}
            {...cycleForm.getInputProps('cycle.stage')}
            className="w-full"
            styles={{
                input: {
                    height: heightToUse
                }
            }}
        />
    );
}

export const CycleStatus = ({ cycleForm, heightQuery }: { cycleForm: CycleFormType, heightQuery: number }) => {

    const data = [
        { value: 'growing', label: 'Growing' },
        { value: 'harvested', label: 'Harvested' },
        { value: 'failed', label: 'Failed' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'abandoned', label: 'Abandoned' },
        { value: 'paused', label: 'Paused' },
        { value: 'not started', label: 'Not Started' }
    ]

    const heightToUse = heightQuery / 15;

    return (
        <Select
            id="cycle-status"
            label="Status"
            placeholder="Select status"
            data={data.sort((a, b) => a.label.localeCompare(b.label))}
            {...cycleForm.getInputProps('cycle.status')}
            className="w-full"
            styles={{
                input: {
                    height: heightToUse
                }
            }}
        />
    );
}

export const CycleDate = ({ cycleForm }: { cycleForm: CycleFormType }) => {

    return (
        <DatePickerInput
            id="attempt-date"
            key={cycleForm.key('cycle.beginCycleDate')}
            label="Beginning Date"
            placeholder="Select date"
            {...cycleForm.getInputProps('cycle.beginCycleDate')} />
    )
}

export const PlantForCycle = ({ cycleForm, plants, heightQuery }: { cycleForm: CycleFormType, plants: IPlant[], heightQuery: number }) => {

    const setGlobalLoading = useStateStore(state => state.setGlobalLoading);
    const setCurrPlant = useStateStore(state => state.setCurrPlant);
    const plant = plants.find(p => p._id === cycleForm.getValues().cycle.plantId) as IPlant;
    const heightToUse = heightQuery / 15;

    return (
        <Select
            id="plant-for-cycle"
            label={
                <div className="flex flex-row justify-between items-center w-[100%]">
                    <p>{'Plant'}</p>
                    {cycleForm.getValues().cycle.plantId ? (
                        <Link href={`/plants/${plant._id}`} className={`text-blue-500 underline text-[14px] hover:text-blue-300 cursor-pointer`} onClick={() => {
                            setGlobalLoading(true);
                            setCurrPlant(plant);
                        }}>
                            {'View Plant Details'}
                        </Link>
                    ) : (
                        <p className="text-gray-300 text-[14px] cursor-not-allowed">
                            {'View Plant Details'}
                        </p>
                    )}

                </div>
            }
            key={cycleForm.key('cycle.plantId')}
            data={plants.map(plant => ({ value: plant._id, label: plant.name }))}
            placeholder="Select a plant"
            {...cycleForm.getInputProps('cycle.plantId')}
            clearable
            disabled={plants.length === 0}
            className="w-full"
            styles={{
                input: { height: heightToUse },
                label: { width: '100%' }
            }}
        />
    )

}